/**
 * Formats plain text book descriptions from Google Books API
 * - Adds paragraph breaks at natural boundaries
 * - Limits length intelligently
 * - Handles special formatting like quotes and lists
 */
export function formatBookDescription(description: string | undefined, maxLength: number = 600): string {
  if (!description) return ''

  let text = description.trim()

  // Truncate if too long
  if (text.length > maxLength) {
    const lastPeriod = text.substring(0, maxLength).lastIndexOf('.')
    const lastQuestion = text.substring(0, maxLength).lastIndexOf('?')
    const lastExclamation = text.substring(0, maxLength).lastIndexOf('!')
    const lastSentenceEnd = Math.max(lastPeriod, lastQuestion, lastExclamation)

    if (lastSentenceEnd > maxLength * 0.7) {
      text = text.substring(0, lastSentenceEnd + 1)
    } else {
      const lastSpace = text.substring(0, maxLength).lastIndexOf(' ')
      text = text.substring(0, lastSpace > 0 ? lastSpace : maxLength) + '...'
    }
  }

  // Add paragraph breaks at natural boundaries
  // 1. Before "One of..." award/review lists (common in book descriptions)
  text = text.replace(/\s+(One of [A-Z][^.]+\.)/g, '\n\n$1')

  // 2. Before date indicators (e.g., "February 1862.")
  text = text.replace(/\s+((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\.)/g, '\n\n$1')

  // 3. Before "Named One of..."
  text = text.replace(/\s+(Named One of [^.]+\.)/g, '\n\n$1')

  // 4. Add spacing around end quotes (reviews at the end)
  text = text.replace(/\s+[""]([^""]+)[""]—([^""]+)$/g, '\n\n"$1"\n—$2')

  return text
}

/**
 * Decodes common HTML entities
 */
function decodeHtmlEntities(text: string): string {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&nbsp;': ' ',
    '&mdash;': '\u2014', // em dash
    '&ndash;': '\u2013', // en dash
    '&hellip;': '\u2026', // ellipsis
    '&rsquo;': '\u2019', // right single quote
    '&lsquo;': '\u2018', // left single quote
    '&rdquo;': '\u201D', // right double quote
    '&ldquo;': '\u201C', // left double quote
  }

  let decoded = text
  Object.entries(entities).forEach(([entity, char]) => {
    decoded = decoded.replace(new RegExp(entity, 'g'), char)
  })

  // Handle numeric entities like &#8217;
  decoded = decoded.replace(/&#(\d+);/g, (_, num) =>
    String.fromCharCode(parseInt(num))
  )

  return decoded
}
